// src/components/ResilienceMonitor.tsx
// ---------------------------------------------------------------------------
// Mounted once at app root. Quietly runs the background reconciliation
// pipeline on a schedule and stamps the centralized error logger with the
// current auth user id.
//
// Production-safe monitoring mode:
//   • All jobs are READ-only by default — we only flag inconsistencies and
//     surface them via window.__kcReconcile and the diagnostic panel.
//   • One-sided spouse links are reported for reviewed Governor repair.
//     Background monitoring never mutates family_graph_members.
//   • Stale cache keys older than 24h are silently purged.
//   • Renders nothing visible. Never blocks the UI.
//
// Per the freeze rule, this file does not introduce any new spouse-
// placement heuristic or renderer change. It only observes.
import React, { useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { setLoggerUserId } from '@/lib/errorLogger';
import { runAllReconciliation } from '@/lib/reconciliation';
import { useFamilyGraph } from '@/contexts/FamilyGraphContext';
import {
  auditExistingTreeStructure,
} from '@/governor/tree-system/ExistingTreeStructuralAudit';
import {
  buildExistingTreeRepairPlan,
} from '@/governor/tree-system/ExistingTreeRepairPlan';
import {
  summarizeExistingTreeRepairPlan,
} from '@/governor/tree-system/ExistingTreeRepairPlanSummary';
import {
  groupExistingTreeRepairPlan,
} from '@/governor/tree-system/ExistingTreeRepairPlanGrouping';
import {
  certifyExistingTreeRepairRenderer,
} from '@/governor/tree-system/ExistingTreeRepairRendererCertification';


const RUN_INTERVAL_MS = 5 * 60 * 1000; // every 5 minutes
const INITIAL_DELAY_MS = 30 * 1000; // wait 30s after mount so initial render is unaffected

const ResilienceMonitor: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const {
    members,
    loading: familyGraphLoading,
    effectiveOwnerUserId,
    viewerMemberId,
  } = useFamilyGraph();
  const runningRef = useRef(false);

  const structuralAudit = useMemo(
    () =>
      auditExistingTreeStructure(
        members.map((member) => ({
          id: member.id,
          ownerUserId: member.ownerUserId,
          claimedByUserId:
            member.claimedByUserId ?? null,
          name: member.name,
          nickname: member.nickname ?? null,
          treeNamePreference:
            member.treeNamePreference,
          relationship:
            member.relationship ?? null,
          relationshipOverride:
            member.relationshipOverride ?? null,
          approvalStatus:
            member.approvalStatus ?? null,
          profileState:
            String(
              member.profileState ??
                'placeholder',
            ),
          side:
            member.side ?? null,
          parentIds: member.parentIds ?? [],
          spouseId: member.spouseId ?? null,
        })),
        viewerMemberId,
      ),
    [
      members,
      viewerMemberId,
    ],
  );

  const structuralRepairPlan = useMemo(
    () =>
      buildExistingTreeRepairPlan(
        structuralAudit,
      ),
    [structuralAudit],
  );

  const structuralRepairPlanSummary = useMemo(
    () =>
      summarizeExistingTreeRepairPlan(
        structuralRepairPlan,
      ),
    [structuralRepairPlan],
  );

  const structuralRepairPlanGrouping = useMemo(
    () =>
      groupExistingTreeRepairPlan(
        structuralRepairPlan,
      ),
    [structuralRepairPlan],
  );

  const structuralRepairRendererCertification =
    useMemo(
      () =>
        certifyExistingTreeRepairRenderer(
          structuralRepairPlanSummary,
          structuralRepairPlanGrouping,
          structuralRepairPlan,
        ),
      [
        structuralRepairPlan,
        structuralRepairPlanGrouping,
        structuralRepairPlanSummary,
      ],
    );

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      familyGraphLoading
    ) {
      return;
    }

    const projection = {
      generatedAt: new Date().toISOString(),
      effectiveOwnerUserId,
      source: 'FamilyGraphContext',
      mode: 'dry-run',
      report: structuralAudit,
    };

    (
      window as unknown as Record<string, unknown>
    ).__kcTreeStructuralAudit = projection;

    window.dispatchEvent(
      new CustomEvent(
        'kc:tree-structural-audit-updated',
        { detail: projection },
      ),
    );

    const repairPlanProjection = {
      generatedAt: projection.generatedAt,
      effectiveOwnerUserId,
      source: 'FamilyGraphContext',
      mode: 'dry-run',
      report: structuralRepairPlan,
      summary: structuralRepairPlanSummary,
      grouping: structuralRepairPlanGrouping,
      rendererCertification:
        structuralRepairRendererCertification,
    };

    (
      window as unknown as Record<string, unknown>
    ).__kcTreeStructuralRepairPlan =
      repairPlanProjection;

    window.dispatchEvent(
      new CustomEvent(
        'kc:tree-structural-repair-plan-updated',
        { detail: repairPlanProjection },
      ),
    );
  }, [
    effectiveOwnerUserId,
    familyGraphLoading,
    structuralAudit,
    structuralRepairPlan,
    structuralRepairPlanSummary,
    structuralRepairPlanGrouping,
    structuralRepairRendererCertification,
  ]);

  // Stamp every error log with the current auth user.
  useEffect(() => {
    setLoggerUserId(userId);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const run = async () => {
      if (runningRef.current) return;
      runningRef.current = true;
      try {
        // Governor authority: background reconciliation is observation-only.
        // Production writes require an explicit reviewed repair execution,
        // never an automatic app-start or five-minute background cycle.
        const reports = await runAllReconciliation(userId, false);
        // If any job actually wrote rows (repaired > 0) — typically the
        // orphan-in-law repair pulling a stranded son-/daughter-in-law
        // into a couple unit — dispatch the existing global refresh
        // event so every active FamilyGraphProvider re-fetches members
        // and the heart connector / placement appears WITHOUT requiring
        // a manual page reload. Pure UI cue — never blocks.
        const wroteSomething = (reports ?? []).some(
          (r) => (r?.repaired ?? 0) > 0,
        );
        if (wroteSomething && !cancelled && typeof window !== 'undefined') {
          try {
            window.dispatchEvent(new CustomEvent('kc:family-graph-refresh'));
          } catch {
            /* CustomEvent unavailable — older runtime; non-fatal */
          }
        }
      } catch {
        /* swallow — diagnostics already logged by individual jobs */
      } finally {
        runningRef.current = false;
      }
    };


    const initial = window.setTimeout(() => {
      if (!cancelled) void run();
    }, INITIAL_DELAY_MS);
    const interval = window.setInterval(() => {
      if (!cancelled && document.visibilityState === 'visible') void run();
    }, RUN_INTERVAL_MS);

    // Allow manual trigger from the console / diagnostic panel.
    const onManual = () => {
      if (!cancelled) void run();
    };
    window.addEventListener('kc:reconcile-now', onManual);

    return () => {
      cancelled = true;
      window.clearTimeout(initial);
      window.clearInterval(interval);
      window.removeEventListener('kc:reconcile-now', onManual);
    };
  }, [userId]);

  return null;
};

export default ResilienceMonitor;
